import { useState } from 'react';

interface SalesConsultModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SalesConsultModal({ isOpen, onClose }: SalesConsultModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证手机号
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('请输入正确的手机号码');
      return;
    }

    if (!formData.name || !formData.company || !formData.phone) {
      alert('请填写所有必填项');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4jc5rb3rief4cfeaak0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          message: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div 
        className="absolute inset-0" 
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* 头部 */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-600 via-blue-600 to-teal-600 text-white px-8 py-6 rounded-t-3xl z-10">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-all cursor-pointer group"
          >
            <i className="ri-close-line text-2xl group-hover:rotate-90 transition-transform duration-300"></i>
          </button>
          <div className="flex items-center">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mr-4 backdrop-blur-sm">
              <i className="ri-customer-service-2-fill text-3xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-1">联系销售顾问</h3>
              <p className="text-white/90 text-sm">专业团队为您提供一对一咨询服务</p>
            </div>
          </div>
        </div>

        {/* 表单内容 */}
        <form id="sales-consult-form" onSubmit={handleSubmit} data-readdy-form className="p-8">
          <div className="space-y-5">
            <div>
              <label htmlFor="sales-name" className="block text-sm font-bold text-gray-800 mb-2">
                您的姓名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="sales-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all text-sm hover:border-gray-300"
                placeholder="请输入您的姓名"
              />
            </div>

            <div>
              <label htmlFor="sales-company" className="block text-sm font-bold text-gray-800 mb-2">
                公司名称
              </label>
              <input
                type="text"
                id="sales-company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all text-sm hover:border-gray-300"
                placeholder="请输入公司名称"
              />
            </div>

            <div>
              <label htmlFor="sales-phone" className="block text-sm font-bold text-gray-800 mb-2">
                联系电话 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="sales-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="^1[3-9]\d{9}$"
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all text-sm hover:border-gray-300"
                placeholder="请输入11位手机号码"
              />
            </div>

            <div>
              <label htmlFor="sales-message" className="block text-sm font-bold text-gray-800 mb-2">
                咨询内容
              </label>
              <textarea
                id="sales-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all text-sm resize-none hover:border-gray-300"
                placeholder="请描述您的需求或问题（选填，最多500字）"
              />
            </div>

            {submitStatus === 'success' && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-700 px-4 py-3.5 rounded-xl text-sm font-semibold flex items-center">
                <i className="ri-checkbox-circle-fill mr-2 text-lg"></i>
                提交成功！我们的销售顾问会尽快与您联系。
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 text-red-700 px-4 py-3.5 rounded-xl text-sm font-semibold flex items-center">
                <i className="ri-error-warning-fill mr-2 text-lg"></i>
                提交失败，请稍后重试或直接拨打我们的客服热线。
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white py-4 rounded-xl font-bold text-base hover:from-teal-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  提交中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill mr-2"></i>
                  提交咨询申请
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-6 flex items-center justify-center">
            <i className="ri-lock-line mr-1"></i>
            您的信息将被加密传输，我们承诺保护您的隐私安全
          </p>
        </form>
      </div>
    </div>
  );
}
